import { useState, type FormEvent } from 'react'
import { api, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

type Status = { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent' } | { kind: 'error', message: string }

export default function Login() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>({ kind: 'idle' })

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email) return
    setStatus({ kind: 'sending' })
    try {
      await api<{ ok: boolean; message: string }>('/auth/request-link.php', {
        method: 'POST',
        body: { email },
      })
      setStatus({ kind: 'sent' })
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error de conexión'
      setStatus({ kind: 'error', message: msg })
    }
  }

  if (status.kind === 'sent') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">Revisa tu correo</h2>
            <p className="text-sm text-slate-500 mb-6">
              Si <strong>{email}</strong> tiene acceso, recibirás un enlace en unos segundos.
              El enlace es válido por 15 minutos.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => { setStatus({ kind: 'idle' }); setEmail('') }}
            >
              Usar otro correo
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/favicon.png"
            alt="LobbyPlay"
            className="w-16 h-16 rounded-2xl mx-auto mb-4 shadow-lg"
          />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">LobbyPlay Admin</h1>
          <p className="text-sm text-slate-500 mt-2">
            Te enviamos un enlace de acceso por correo
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5" htmlFor="email">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                  autoFocus
                  disabled={status.kind === 'sending'}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={status.kind === 'sending' || !email}
              >
                {status.kind === 'sending' ? 'Enviando…' : 'Enviar enlace'}
              </Button>

              {status.kind === 'error' && (
                <div className={cn('text-sm rounded-md p-3 text-center bg-red-50 text-red-700 border border-red-100')}>
                  {status.message}
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 text-center mt-6">
          Sin contraseña — solo necesitas acceso a tu correo.
        </p>
      </div>
    </div>
  )
}
